import ReactCalendar, {
  CalendarProps,
  CalendarTileProperties,
  ViewCallbackProperties,
} from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styled from "@emotion/styled";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useGetThemeColors } from "../../ChakraTheme";

interface IProps extends CalendarProps {
  id: string;
  onMonthChange?: (date: Date) => void;
  onYearChange?: (date: Date) => void;
  onDayChange?: (date: Date) => void;
  disabledDay?: (date: Date) => boolean;
}

const Calendar = ({
  id,
  onMonthChange,
  onYearChange,
  onDayChange,
  disabledDay,
  ...restProps
}: IProps) => {
  const colors = useGetThemeColors();
  const borderColor = useColorModeValue(colors.primary[600], colors.white);
  const disabledBackgroundColor = useColorModeValue(
    "#f0f0f0",
    colors.gray[300]
  );
  const disabledTextColor = useColorModeValue(colors.white, colors.black);
  const textColor = useColorModeValue(colors.black, colors.white);
  const selectedBackgroundColor = useColorModeValue(
    colors.primary[800],
    colors.gray[800]
  );

  const weekendDayTextColor = useColorModeValue("#d10000", colors.red[400]);

  const disabledWeekendDayTextColor = useColorModeValue(
    "#d10000",
    colors.red[100]
  );

  const onActiveStartDateChange = (props: ViewCallbackProperties) => {
    switch (props.view) {
      case "month":
        return onMonthChange && onMonthChange(props.activeStartDate);
      case "year":
        return onYearChange && onYearChange(props.activeStartDate);
    }
  };

  const tileDisabled = (props: CalendarTileProperties) => {
    if (props.view === "month") {
      return disabledDay ? disabledDay(props.date) : false;
    }
    return false;
  };

  return (
    <StyledCalendar
      id={id}
      borderColor={borderColor}
      disabledBackgroundColor={disabledBackgroundColor}
      disabledTextColor={disabledTextColor}
      textColor={textColor}
      selectedBackgroundColor={selectedBackgroundColor}
      weekendDayTextColor={weekendDayTextColor}
      disabledWeekendDayTextColor={disabledWeekendDayTextColor}
    >
      <ReactCalendar
        onChange={onDayChange}
        onActiveStartDateChange={onActiveStartDateChange}
        tileDisabled={tileDisabled}
        {...restProps}
      />
    </StyledCalendar>
  );
};

const StyledCalendar = styled(Box)<{
  borderColor: string;
  disabledBackgroundColor: string;
  disabledTextColor: string;
  textColor: string;
  selectedBackgroundColor: string;
  weekendDayTextColor: string;
  disabledWeekendDayTextColor: string;
}>`
  .react-calendar {
    background-color: transparent;
    border-radius: 8px;
    width: 100%;
    min-height: 360px;
    border: 2px solid ${({ borderColor }) => borderColor};
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    visibility: hidden;
  }

  .react-calendar__month-view__days__day {
    border: 2px solid transparent;
    color: ${({ textColor }) => textColor};
  }

  .react-calendar__month-view__days__day:disabled {
    background-color: ${({ disabledBackgroundColor }) =>
      disabledBackgroundColor};
    color: black;
  }

  .react-calendar__month-view__days__day:hover:disabled {
    cursor: not-allowed;
  }

  .react-calendar__month-view__days__day:hover:not(:disabled):not(.react-calendar__tile--active) {
    background-color: transparent !important;
    border: 2px solid
      ${({ selectedBackgroundColor }) => selectedBackgroundColor};
    border-radius: 5px;
  }

  .react-calendar__month-view__days__day.react-calendar__tile--active {
    background: ${({ selectedBackgroundColor }) => selectedBackgroundColor};
    color: white;
  }

  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: ${({ selectedBackgroundColor }) => selectedBackgroundColor};
  }

  .react-calendar__navigation__prev-button:disabled,
  .react-calendar__navigation__prev2-button:disabled,
  .react-calendar__navigation__next-button:disabled,
  .react-calendar__navigation__next2-button:disabled {
    color: black;
    cursor: not-allowed;
  }

  .react-calendar__navigation__prev2-button {
    border-top-left-radius: 6px;
  }

  .react-calendar__navigation__next2-button {
    border-top-right-radius: 6px;
  }

  .react-calendar__month-view__days__day--weekend {
    color: ${({ disabledWeekendDayTextColor }) => disabledWeekendDayTextColor};
  }

  .react-calendar__month-view__days__day--weekend:disabled {
    color: ${({ weekendDayTextColor }) => weekendDayTextColor};
  }

  .react-calendar__year-view__months__month:disabled,
  .react-calendar__century-view__decades__decade:disabled {
    color: black;
  }
`;

export { Calendar };
